import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { consume } from '@lit/context';

import '@awesome.me/webawesome/dist/components/button/button.js';

import NSJobComponent from '../../../job.js';
import JobSchema from '../../../../../models/job-schema.js';
import type Job from '../../../../../models/job.js';
import type { PublicLedgerCreditPurchase } from '../../../../../models/public-ledger.js';
import definition from '@job-schemas/public-ledger/credits/buy.json';
import { jobSchema } from '../../../job-schema.js';
import { nsStripeServiceContext } from '../../../../../ns-services/contexts.js';
import { NSStripeService } from '../../../../../ns-services/ns-stripe-service.js';
import { createLogger } from '../../../../../ns-services/logger.js';
import { claimJob, isUnclaimed } from '../../../../../helpers/job-claim.js';
import { formatCredits } from '../../../../../helpers/public-ledger-credits.js';
import { lambdaRefusal } from '../../../../../helpers/public-ledger-balance-api.js';
import { payCredits, quoteCredits, type CreditQuoteAnswer } from '../../../../../helpers/public-ledger-credits-api.js';
import {
  MIN_CREDITS,
  deliverableNowCredits,
  formatPence,
  formatUnitPence,
  isQuoteLive,
  partialDeliveryMessage,
  purchaseRefusalLine,
  purchaseMinimum,
  purchaseProgressMessage,
  totalPence,
  validateCredits,
} from '../../../../../helpers/credit-pricing.js';

import '../../../../../ns-elements/ns-job-layout.js';
import '../../../../../ns-elements/ns-detail-list.js';
import '../../../../../ns-elements/ns-credits-input.js';
import '../../../../../ns-elements/ns-quote-validity.js';

const logger = createLogger('ns-public-ledger-buy-credits');

// The lambda Stripe returns the browser to after a redirect (3DS); it fulfils the purchase and redirects back
const PAYMENT_RETURN_PATH = 'public-ledger/credits/payment-return';

/**
 * Buy nodeStream credits (Q23): an amount of at least 10,000 credits, a price held for 30 minutes at the mainnet rate, a
 * live total in pounds and payment by a card entered on the spot (no saved card, #25 DECISION 2.7). The server completes
 * the job paid once Stripe confirms the payment; the card then follows the purchase's delivery.
 */
@customElement(NSPublicLedgerBuyCredits.is)
export default class NSPublicLedgerBuyCredits extends NSJobComponent {
  static get is() {
    return 'ns-public-ledger-buy-credits';
  }

  static get schema(): JobSchema {
    // The server completes the job once the payment is confirmed, never an outcome button
    return jobSchema(definition, { outcomes: { paid: { type: 'hidden' } }, icon: 'transaction' });
  }

  static styles = [
    ...super.styles,
    css`
      .job-panel ns-detail-list {
        margin: var(--wa-space-s) 0;
        --ns-detail-list-gap: var(--wa-space-2xs);
      }

      .job-panel ns-quote-validity {
        margin-top: var(--wa-space-s);
      }
    `,
  ];

  @consume({ context: nsStripeServiceContext })
  private __stripe?: NSStripeService;

  @state()
  private __credits: number | null = MIN_CREDITS;

  // Whether the person has changed the amount; until then it follows the quote's minimum
  private __creditsTouched = false;

  @state()
  private __quote: CreditQuoteAnswer | null = null;

  @state()
  private __quoting = false;

  // A purchase the server refuses outright, shown in place of the price and payment: a MAINNET company outside production
  // can't pay here (concern 2), or #41 the company's automatic payments aren't on
  @state()
  private __refusal: string | null = null;

  // A client secret once the Payment Element is mounted for a new card
  @state()
  private __clientSecret: string | null = null;

  @state()
  private __busy = false;

  // The card has been sent to Stripe (confirmed in the Payment Element): only then does a PAYMENT_PENDING
  // purchase show as waiting for confirmation; before that the card still needs entering
  @state()
  private __submitted = false;

  @state()
  private __purchase: PublicLedgerCreditPurchase | null = null;

  async connectedCallback() {
    await super.connectedCallback();
    const job = this._job;
    if (!job) return;
    if (job.status !== 'COMPLETE' && !isUnclaimed(job)) await this.__requote();
    await this.__loadPurchase();

    this._subscriptions.subscribe(`job.${job.id}.*`, () => {
      const updated = this._db?.get(`job.${job.id}`) as Job | undefined;
      if (updated) {
        this._job = updated;
        this.requestUpdate();
      }
    });
    this._subscriptions.subscribe('plCreditPurchase.*', () => {
      void this.__loadPurchase();
    });
  }

  disconnectedCallback() {
    this.__stripe?.cleanElements();
    super.disconnectedCallback();
  }

  private async __loadPurchase() {
    const jobId = this._job?.id;
    if (!jobId || !this._db) return;
    try {
      const found = await this._db.query('plCreditPurchase', { jobId: { $eq: jobId } });
      const [purchase] = (found?.results ?? []) as unknown as PublicLedgerCreditPurchase[];
      this.__purchase = purchase ?? null;
    } catch (err) {
      logger.warn('Unable to load the purchase', err);
    }
  }

  private async __requote() {
    const job = this._job;
    if (!job || !this._auth?.token || this.__quoting) return;
    this.__quoting = true;
    this.clearError();
    try {
      this.__quote = await quoteCredits(this._auth.token, { jobId: job.id });
      if (!this.__creditsTouched) this.__credits = this.__minimum;
      this.__refusal = null;
    } catch (err) {
      const refusal = purchaseRefusalLine(err);
      if (refusal) {
        this.__refusal = refusal;
      } else {
        this.showError(lambdaRefusal(err, 'Unable to get a price. Please try again.'));
      }
    } finally {
      this.__quoting = false;
    }
  }

  private async __claim() {
    const token = this._auth?.token;
    if (!this._job || !token || this.__busy) return;
    this.__busy = true;
    this.clearError();
    try {
      await claimJob(token, this._job.id);
      this._job = { ...this._job, assignedTo: this._directory?.me?.id };
      await this.__requote();
    } catch (err) {
      this.showError(lambdaRefusal(err, 'Unable to claim this job.'));
    } finally {
      this.__busy = false;
    }
  }

  private get __minimum() {
    return purchaseMinimum(this.__quote);
  }

  private get __quoteLive() {
    return isQuoteLive(this.__quote);
  }

  private get __canPay() {
    return (
      !this.__busy && !this.__refusal && this.__quoteLive && validateCredits(this.__credits, this.__minimum) === null
    );
  }

  // Starts the payment: the Payment Element is mounted for the card, and Pay then sends it to Stripe
  private async __pay() {
    const job = this._job;
    const quote = this.__quote;
    const credits = this.__credits;
    if (!job || !quote || credits === null || !this.__canPay || !this._auth?.token) return;
    this.__busy = true;
    this.clearError();
    this.loading('Starting the payment');
    try {
      const answer = await payCredits(this._auth.token, { jobId: job.id, credits });
      if (!answer.clientSecret) throw new Error('The payment could not be started. Please try again.');
      this.__clientSecret = answer.clientSecret;
      await this.updateComplete;
      // The Stripe service is shared: a form another card left behind would otherwise be reused, slotted into that card
      this.__stripe?.cleanElements();
      await this.__stripe?.mountStripeElement(this, answer.clientSecret);
      await this.__loadPurchase();
    } catch (err) {
      const refusal = purchaseRefusalLine(err);
      if (refusal) {
        this.__refusal = refusal;
        return;
      }
      this.showError(lambdaRefusal(err, 'The payment could not be completed. Please try again.'));
      if (lambdaRefusal(err, '').toLowerCase().includes('quote')) await this.__requote();
    } finally {
      this.loaded();
      this.__busy = false;
    }
  }

  private async __confirmNewCard() {
    if (!this._job || !this.__stripe || this.__busy) return;
    this.__busy = true;
    this.loading('Processing payment');
    try {
      // false: Stripe refused the form (it shows why in the form), so the card still needs correcting
      this.__submitted = await this.__stripe.proceedPayment(this._job.id, PAYMENT_RETURN_PATH);
      await this.__loadPurchase();
    } catch (err) {
      this.showError(err);
    } finally {
      this.loaded();
      this.__busy = false;
    }
  }

  private __renderProgress() {
    const message = purchaseProgressMessage(this.__purchase);
    if (!message) return nothing;
    return html`
      <div class="job-panel" role="status">
        <h3>Your purchase</h3>
        <p>${message}</p>
      </div>
    `;
  }

  private __renderPrice() {
    if (this.__refusal) {
      return html`
        <div class="job-panel">
          <p role="alert">${this.__refusal}</p>
        </div>
      `;
    }
    const quote = this.__quote;
    const credits = this.__credits;
    const invalid = validateCredits(credits, this.__minimum);
    const unit = quote?.unitPricePence ?? null;
    const total = unit !== null && credits !== null && !invalid ? totalPence(credits, unit) : null;
    const partial = invalid
      ? null
      : partialDeliveryMessage(credits, deliverableNowCredits(credits, quote?.availableCredits, quote?.owedCredits));
    return html`
      <div class="job-panel">
        <h3>How many credits?</h3>
        <ns-credits-input
          required
          .credits=${this.__credits}
          ?disabled=${!!this.__clientSecret}
          .error=${this.__credits !== null && invalid ? invalid : ''}
          @credits-change=${(e: CustomEvent<{ credits: number | null }>) => {
            this.__credits = e.detail.credits;
            this.__creditsTouched = true;
          }}
        ></ns-credits-input>
        <ns-detail-list
          .rows=${[
            { label: 'Price per credit', value: unit !== null ? formatUnitPence(unit) : undefined },
            { label: 'Total', value: total !== null ? formatPence(total) : undefined },
          ]}
        ></ns-detail-list>
        ${partial
          ? html`
              <p class="note">${partial}</p>
            `
          : nothing}
        <ns-quote-validity
          .expiresAt=${quote?.expiresAt ?? null}
          ?refreshing=${this.__quoting}
          @quote-expired=${() => this.requestUpdate()}
          @quote-refresh=${() => this.__requote()}
        ></ns-quote-validity>
      </div>
      ${this.__clientSecret
        ? html`
            <div class="job-panel">
              <slot name="stripe-payment-form"></slot>
            </div>
          `
        : nothing}
    `;
  }

  render() {
    const job = this._job;
    const status = this.__purchase?.status;
    const paid =
      job?.status === 'COMPLETE' ||
      (!!status && status !== 'QUOTED' && (status !== 'PAYMENT_PENDING' || this.__submitted));
    return html`
      <ns-job-layout>
        <p slot="intro">
          nodeStream credits pay for your company's use of the public ledger. Choose how many to buy (at least
          ${formatCredits(this.__minimum)}) and pay by card. They're added to your company's public ledger account.
        </p>
        ${paid ? this.__renderProgress() : job && !isUnclaimed(job) ? this.__renderPrice() : nothing}
      </ns-job-layout>
    `;
  }

  renderActions() {
    const job = this._job;
    if (!job || job.status === 'COMPLETE') return nothing;
    if (isUnclaimed(job)) {
      return html`
        <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__claim()}>
          Claim
        </wa-button>
      `;
    }
    if (this.__purchase && !['QUOTED', 'PAYMENT_PENDING'].includes(this.__purchase.status ?? '')) return nothing;
    if (this.__clientSecret) {
      return html`
        <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__confirmNewCard()}>
          Pay
        </wa-button>
      `;
    }
    return html`
      <wa-button variant="brand" type="button" ?disabled=${!this.__canPay} @click=${() => this.__pay()}>Pay</wa-button>
    `;
  }
}
